// DS: PO30
import React, { useContext, useState, useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ModalFormDialog, {
  ModalFormDialogProps,
} from '@wix/da-shared-react/pkg/Popup/ModalFormDialog';
import { POINTS_MAX_GIVE_TO_OTHER_DEVIANT_VALUE } from '@wix/da-shared-react/pkg/utils/points';
import BooleanInput from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import PointsInput from '@wix/da-shared-react/pkg/PointsInput';
import Textarea from '@wix/da-shared-react/pkg/Textarea';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import FooterExtras from './FooterExtras';
import useModuleMeta from '../../../../../utils/hooks/useModuleMeta';
import s from './DonatePointsPopup.scss';

const INIT_POINTS_VALUE = 80;

export type Props = {
  username: string;
  moduleId: number;
  donatePoints?: (payload) => void;
  close?: () => void;
} & Omit<ModalFormDialogProps, 'children'>;

export const DonatePointsPopup: React.FC<Props> = ({
  username,
  moduleId,
  donatePoints,
  close,
  ...formPopupProps
}) => {
  const { t } = useTranslation();
  const {
    moduleMeta: { isDonationSuccess = false, isDonating, donationErrorMessage },
  } = useModuleMeta(moduleId);
  const isMobile = useContext(MobileContext);
  const [pointsAmount, setPointsAmount] = useState(INIT_POINTS_VALUE);
  const [pointsInputError, setPointsInputError] = useState<string | null>(null);
  const [isPrivate, setIsPrivate] = useState<boolean | null>(null);
  const [textareaValue, setTextareaValue] = useState<string | null>(null);

  const onSubmit = useCallback(() => {
    donatePoints &&
      donatePoints({
        moduleId,
        isPrivate,
        amount: pointsAmount,
        message: textareaValue,
      });
  }, [donatePoints, isPrivate, moduleId, pointsAmount, textareaValue]);

  const onClose = useCallback(() => close && close(), [close]);

  useEffect(() => {
    if (isDonationSuccess) {
      onClose();
    }
  }, [isDonationSuccess, onClose]);

  const maxValue = POINTS_MAX_GIVE_TO_OTHER_DEVIANT_VALUE;

  return (
    <ModalFormDialog
      isOpen={true}
      title={
        isMobile
          ? t('givePointsPopup.title.mobile')
          : t('givePointsPopup.title', { username: username })
      }
      subtitle={isMobile ? undefined : t('givePointsPopup.subtitle')}
      submitBtnLabel={t('givePointsPopup.button.primary')}
      disabledSubmit={!!pointsInputError || isDonating}
      onClose={onClose}
      onCancel={onClose}
      footerExtras={<FooterExtras />}
      onSubmit={onSubmit}
      submitBiData={{
        evid: BiEventType.GIVE_POINTS_CLICK,
        points_total: pointsAmount,
        sent_personal_message:
          textareaValue && textareaValue.length > 0 ? 1 : 0,
      }}
      {...formPopupProps}
    >
      <div className={s['content-wrapper']}>
        <div className={s['give-points-to-user']}>
          <Trans i18nKey="givePointsPopup.givePointsToUser">
            Donate Points to <strong>{{ username }}</strong>
          </Trans>
        </div>
        <div className={s['give-points-motivation']}>
          {t('givePointsPopup.givePointsMotivation')}
        </div>
        <PointsInput
          className={s['points-input']}
          label={t('givePointsPopup.pointsInputLabel')}
          value={pointsAmount}
          maxValue={maxValue}
          errorMessage={pointsInputError}
          onChange={(value, error) => {
            setPointsAmount(value);
            if (error) {
              setPointsInputError(error);
            } else {
              setPointsInputError(null);
            }
            if (value > maxValue) {
              // custom max value error
              setPointsInputError(
                t('givePointsPopup.error.biggerThanMaxValue', { maxValue })
              );
            }
          }}
        />
        <Textarea
          value={textareaValue}
          onChange={value => setTextareaValue(value)}
          wrapperClassName={s['textarea-wrapper']}
          placeholder={t('givePointsPopup.textarea.placeholder')}
          maxLength={250}
        />
        <div className={s['checkbox-wrapper']}>
          <BooleanInput
            id="visible-to-user"
            label={t('givePointsPopup.makeOnlyVisibleToUser', { username })}
            value={!!isPrivate}
            size="large"
            onChange={setIsPrivate}
          />
        </div>
        {donationErrorMessage && (
          <div className={s['error-message']}>{t(donationErrorMessage)}</div>
        )}
      </div>
    </ModalFormDialog>
  );
};
DonatePointsPopup.displayName = 'DonatePointsPopup';

export default DonatePointsPopup;
